import { React, useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";

/*******INTERNAL IMPORTS********/
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { FormPage, ButtonGroup, TabPanel, TableModal, notifyToast, ToastType } from "../../../components-old/ComponentsIndex";
import { DisplaySpinnerContext } from "../../../context/ContextIndex";
import { selectSessionConfig, selectUser } from "../../../store/store-selector";
import { useFetchListData } from "../../../hooks/useFetchListData";
import { approveChange, rejectChange } from "../../review/api-handler/api-handler";
import { IsLoggedInUserPricingApprover } from "../../../hoc/withAuthRedirect";
import { AppUtility } from "../../../utils/utility";

const ReviewDetailsForm = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const navigationData = location.state;
    const [searchParams] = useSearchParams();
    const reviewFilter = searchParams.get("reviewFilter");
    const spinnerCtx = useContext(DisplaySpinnerContext);
    const settingsConfig = useSelector(selectSessionConfig);
    const [selReviewDetails, setSelReviewDetails] = useState([]);
    const [recordAction, setRecordAction] = useState(null);
    const [showApproveRejectBtns, setShowApproveRejectBtns] = useState(false);
    const [selectedTab, setSelectedTab] = useState("0");
    const user = useSelector(selectUser);

    const ReviewDetailsList = useFetchListData(
        settingsConfig.businessCode,
        settingsConfig.countryCode,
        "getAuditInfoList",
        "auditinfovantageportal",
        { required: true, clause: reviewFilter }
    );

    //on tab change
    const handleTabChange = (newTab) => {
        setSelectedTab(newTab);
    };

    const handleCancel = () => {
        try {
            navigate(navigationData.previousPath);
        } catch (err) {
            alert(err);
        }
    };

    const handleApprove = async () => {
        try {
            spinnerCtx.setLoadingIndicator(true);
            const status = await approveChange(ReviewDetailsList[0]);
            if (status?.responseStatus === 200) {
                spinnerCtx.setLoadingIndicator(false);
                notifyToast("Approved", ToastType.SUCCESS);
                handleCancel();
            } else {
                notifyToast("Action failed", ToastType.ERROR);
                spinnerCtx.setLoadingIndicator(false);
            }
        } catch (err) {
            alert(err);
        }
    };

    const handleReject = async () => {
        try {
            spinnerCtx.setLoadingIndicator(true);
            const status = await rejectChange(ReviewDetailsList[0]);
            if (status?.responseStatus === 200) {
                spinnerCtx.setLoadingIndicator(false);
                notifyToast("Rejected", ToastType.SUCCESS);
                handleCancel();
            } else {
                notifyToast("Action failed", ToastType.ERROR);
                spinnerCtx.setLoadingIndicator(false);
            }
        } catch (err) {
            alert(err);
        }
    };

    const isLoggedInUserApprover = IsLoggedInUserPricingApprover();

    const cancelButtonProps = (isLoggedInUserApprover && showApproveRejectBtns) ? [
        {
            buttonText: t("generic.approve"),
            onClick: handleApprove,
            buttonType: "outlined",
            buttonColor: "#A62B23",
            textColor: "#A62B23",
        },
        {
            buttonText: t("generic.reject"),
            onClick: handleReject,
            buttonType: "outlined",
            buttonColor: "#A62B23",
            textColor: "#A62B23",
        },
        {
            buttonText: t("popup.close"),
            onClick: handleCancel,
            buttonType: "outlined",
            buttonColor: "#A62B23",
            textColor: "#A62B23",
        }] : [{
            buttonText: t("popup.close"),
            onClick: handleCancel,
            buttonType: "outlined",
            buttonColor: "#A62B23",
            textColor: "#A62B23",
        }];

    const tableColumnSchema = [
        {
            title: 'Field',
            dataIndex: 'fieldName'
        },
        {
            title: 'Original Value',
            dataIndex: 'previousValue',
            render: (text, record) => `${text}`, //To allow boolean values to display in the table.
            hidden: (recordAction ? recordAction === "CREATE" : false)
        },
        {
            title: 'New Value',
            dataIndex: 'currentValue',
            render: (text, record) => `${text}`,
            hidden: (recordAction ? recordAction === "DELETE" : false)
        },
        {
            title: 'Action',
            dataIndex: 'action',
            render: (text, record) => {
                return {
                    props: {
                        style: { color: text ? "green" : "" }
                    },
                    children: <div>{text}</div>
                };
            }
        }
    ];

    const reviewTabPanelConfigs = selReviewDetails.map((item) => {
        return {
            icon: "",
            label: item.tabName,
            tabPanelStyle: {
                paddingLeft: "0px",
                paddingRight: "0px",
                paddingBottom: "0px",
                paddingTop: "15px"
            },
            children: (
                <TableModal
                    rows={item.tabValues}
                    columns={tableColumnSchema}
                />
            ),
        };
    });

    //setting data in list
    const setReviewDetails = (recordlist) => {
        try {
            recordlist.sort((a, b) => new Date(b.CreatedDate).getTime() - new Date(a.CreatedDate).getTime());
            console.log("recordlist ::::", recordlist[0]);
            let _reviewDetailRec = recordlist[0];

            let _currentStateObj = (_reviewDetailRec.CurrentState) ? JSON.parse(_reviewDetailRec.CurrentState) : {};
            let _previousStateObj = (_reviewDetailRec.PreviousState) ? JSON.parse(_reviewDetailRec.PreviousState) : {};

            let _fieldNamesArr = (Object.keys(_currentStateObj).length > 0) ? Object.keys(_currentStateObj) : Object.keys(_previousStateObj);

            let _mainTableReviewDetails = _fieldNamesArr.filter((item) => (typeof _currentStateObj[item] !== "object" && typeof _previousStateObj[item] !== "object"))
                .map((item) => {
                    return {
                        key: item,
                        fieldName: AppUtility.toTitleCase(item),
                        currentValue: (_currentStateObj[item] !== undefined) ? _currentStateObj[item] : "",
                        previousValue: (_previousStateObj[item] !== undefined) ? _previousStateObj[item] : ""
                    };
                });

            _mainTableReviewDetails[0].action = _reviewDetailRec.Action;
            let _finalReviewDetails = [];
            _finalReviewDetails.push({ tabName: _reviewDetailRec.Location, tabValues: _mainTableReviewDetails });

            //Dependent Tables
            let _dependentTabNames = _fieldNamesArr.filter((item) => (typeof _currentStateObj[item] === "object" || typeof _previousStateObj[item] === "object"));
            console.log("_dependentTabNames ::::", _dependentTabNames);

            let _loopingObjForCreateDelete = (_reviewDetailRec.Action === "CREATE") ? _currentStateObj : _previousStateObj;

            for (let j = 0; j < _dependentTabNames.length; j++) {
                let _depTabName = _dependentTabNames[j];
                let _depTabValues = [];
                if (_reviewDetailRec.Action === "CREATE" || _reviewDetailRec.Action === "DELETE") {
                    for (let k = 0; k < _loopingObjForCreateDelete[_depTabName].length; k++) {
                        let _rec = _loopingObjForCreateDelete[_depTabName][k];
                        let _fieldNamesArr = Object.keys(_rec);
                        for (let l = 0; l < _fieldNamesArr.length; l++) {
                            if (_fieldNamesArr[l] !== "Id" && _fieldNamesArr[l] !== "Action") {
                                _depTabValues.push({
                                    key: _depTabValues.length,
                                    fieldName: AppUtility.toTitleCase(_fieldNamesArr[l]),
                                    currentValue: (_reviewDetailRec.Action === "CREATE") ? _rec[_fieldNamesArr[l]] : "",
                                    previousValue: (_reviewDetailRec.Action === "DELETE") ? _rec[_fieldNamesArr[l]] : "",
                                    action: (l === 0) ? _rec.Action : ""
                                });
                            }
                        }
                        _depTabValues.push({ key: _depTabValues.length, fieldName: "", currentValue: "", previousValue: "" }); //pushing an empty row to create a seperation if multiple records are available
                    }
                } else {
                    //In edit scenario
                    for (let k = 0; k < _currentStateObj[_depTabName].length; k++) { //with the assumption that currentStateObj is always >= previousStateObj length
                        let _currRec = _currentStateObj[_depTabName][k];
                        let _fieldNamesArr = Object.keys(_currRec);
                        let _isRecMatched = false;
                        if (_previousStateObj[_depTabName]) {
                            for (let m = 0; m < _previousStateObj[_depTabName].length; m++) {
                                let _prevRec = _previousStateObj[_depTabName][m];
                                if (_currRec.Id === _prevRec.Id) { //We need to match the id of the record to fetch the correct link between current and previous Obj
                                    _isRecMatched = true;
                                    for (let l = 0; l < _fieldNamesArr.length; l++) {
                                        if (_fieldNamesArr[l] !== "Id" && _fieldNamesArr[l] !== "Action") { //Id field will be available when the record is edited/deleted
                                            _depTabValues.push({
                                                key: _depTabValues.length,
                                                fieldName: AppUtility.toTitleCase(_fieldNamesArr[l]),
                                                currentValue: (_currRec[_fieldNamesArr[l]]) ? _currRec[_fieldNamesArr[l]] : "",
                                                previousValue: (_prevRec[_fieldNamesArr[l]]) ? _prevRec[_fieldNamesArr[l]] : "",
                                                action: (l === 0) ? _currRec.Action : ""
                                            });
                                        }
                                    }
                                    _depTabValues.push({ key: _depTabValues.length, fieldName: "", currentValue: "", previousValue: "" }); //pushing an empty row to create a seperation if multiple records are available
                                    break;
                                }
                            }
                        }
                        if (!_isRecMatched) { //there are newly created records in currentObj
                            for (let l = 0; l < _fieldNamesArr.length; l++) {
                                if (_fieldNamesArr[l] !== "Action") {
                                    _depTabValues.push({
                                        key: _depTabValues.length,
                                        fieldName: AppUtility.toTitleCase(_fieldNamesArr[l]),
                                        currentValue: _currRec[_fieldNamesArr[l]],
                                        previousValue: "",
                                        action: (l === 0) ? _currRec.Action : ""
                                    });
                                }
                            }
                            _depTabValues.push({ key: _depTabValues.length, fieldName: "", currentValue: "", previousValue: "" }); //pushing an empty row to create a seperation if multiple records are available
                        }
                    }
                }
                console.log("_depTabValues ::::", _depTabValues);
                _finalReviewDetails.push({ tabName: AppUtility.toTitleCase(_depTabName), tabValues: _depTabValues });
            }

            console.log("_finalReviewDetails ::::", _finalReviewDetails);
            setSelReviewDetails(_finalReviewDetails);
            setRecordAction(_reviewDetailRec.Action);
            let _loggedInEmail = user.userRecord.email;
            let _showApproveRejectBtns = false;
            if (_loggedInEmail !== _reviewDetailRec.UserEmail) {
                _showApproveRejectBtns = (_reviewDetailRec.Status === "APPROVED" || _reviewDetailRec.Status === "REJECTED") ? false : true;
            }
            setShowApproveRejectBtns(_showApproveRejectBtns);
            spinnerCtx.setLoadingIndicator(false);
        } catch (err) {
            alert(err);
        }
    };

    /**************************************************
    //Form on load useEffect
    **************************************************/
    useEffect(() => {
        spinnerCtx.setLoadingIndicator(true);
        if (ReviewDetailsList && ReviewDetailsList.length > 0) {
            setReviewDetails(ReviewDetailsList);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ReviewDetailsList]);

    return (
        <FormPage>
            <TabPanel
                tabs={reviewTabPanelConfigs}
                selectedTab={selectedTab}
                onTabChange={handleTabChange}
            />
            <div style={{
                "display": "flex",
                "justify-content": "flex-end"
            }}>
                <ButtonGroup buttons={cancelButtonProps} />
            </div>
        </FormPage>
    );
};

export default ReviewDetailsForm;